import React, { useCallback, useEffect, useRef, useState } from 'react';
import type {
  FilterWrapperParams,
  IAfterGuiAttachedParams,
} from 'ag-grid-community';
import type { CustomFilterDisplayProps } from 'ag-grid-react';
import { useGridFilterDisplay } from 'ag-grid-react';
import { Icon } from '@wk/components-v3-react16';
import { useAtom } from 'jotai';
import { dashboardFilterAtom } from '../../filterAtom';
import { useStatusConfig } from '../../useStatusSelection';
import { MAP_STATUS_ICON } from '../../StatusButtons/iconsConstants';
import styles from './StatusFilter.module.scss';

type StatusType = keyof typeof MAP_STATUS_ICON;

interface StatusFilterModel {
  jobStatuses: string[];
}

interface StatusFilterProperties
  extends CustomFilterDisplayProps<StatusFilterModel>,
    FilterWrapperParams {}

const StatusFilter = (properties: StatusFilterProperties) => {
  const { model, onStateChange } = properties;
  const { statusConfig } = useStatusConfig();
  const [filters, setFilters] = useAtom(dashboardFilterAtom);
  const containerReference = useRef<HTMLDivElement>(null);

  // Local state for the filter UI (not yet applied)
  const [localSelectedStatus, setLocalSelectedStatus] = useState<string[]>(
    model?.jobStatuses || filters.jobStatuses
  );

  const afterGuiAttached = useCallback(
    (parameters?: IAfterGuiAttachedParams) => {
      if (!parameters?.suppressFocus) {
        containerReference.current?.focus();
      }
    },
    []
  );

  useGridFilterDisplay({
    afterGuiAttached,
  });

  // Sync local state with dashboardFilterAtom when it changes
  // This ensures the filter UI always reflects the actual applied filter
  useEffect(() => {
    setLocalSelectedStatus(filters.jobStatuses);
  }, [filters.jobStatuses]);

  // Apply filters when model changes (when Apply button is clicked)
  const previousModelReference = useRef<StatusFilterModel | null>(null);

  useEffect(() => {
    const currentStatuses = model?.jobStatuses || ['ANY'];
    const previousStatuses = previousModelReference.current?.jobStatuses || [
      'ANY',
    ];

    const hasChanged =
      JSON.stringify(currentStatuses) !== JSON.stringify(previousStatuses);

    if (hasChanged) {
      // Apply to dashboard filter atom
      setFilters((previousFilter) => ({
        ...previousFilter,
        activePage: 1,
        jobStatuses: currentStatuses,
      }));
      previousModelReference.current = model;
    }
  }, [model, setFilters]);

  const handleStatusFilterClick = useCallback(
    (status: string) => {
      // Only update local state and AG Grid model
      const newStatuses = [status];
      setLocalSelectedStatus(newStatuses);
      const newModel = { jobStatuses: newStatuses };
      onStateChange({ model: newModel });
      // Don't apply to atoms yet - that happens when Apply is clicked
    },
    [onStateChange]
  );

  const isActiveStatus = useCallback(
    (status: string) => localSelectedStatus.includes(status),
    [localSelectedStatus]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, status: string) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleStatusFilterClick(status);
      }
    },
    [handleStatusFilterClick]
  );

  return (
    <div className={styles.container} ref={containerReference} tabIndex={-1}>
      {statusConfig.map(({ status, label, count }) => {
        const iconConfig =
          status === 'ANY' ? null : MAP_STATUS_ICON[status as StatusType];
        const isActive = isActiveStatus(status);

        return (
          <div
            key={status}
            onClick={() => handleStatusFilterClick(status)}
            onKeyDown={(event) => handleKeyDown(event, status)}
            role="button"
            tabIndex={0}
            className={`${styles.statusButton} ${
              isActive ? styles.isActive : ''
            }`}
          >
            {iconConfig && (
              <span
                className={styles.iconWrapper}
                style={{ color: iconConfig.color, fontSize: 16 }}
              >
                <Icon name={iconConfig.name} />
              </span>
            )}
            <div className={styles.label}>
              {label} ({count})
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StatusFilter;
